/**
 * useOvertimeReport Hook
 * 
 * Manages state and logic for overtime (HHEE) reports
 * Includes overtime report data fetching, filtering, pagination, and table configuration
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui/useUI';
import { useMonthlyCutsLoader } from '@/hooks/attendance';
import { overtimeReportService } from '@/api/domains/overtime/overtimeReport.service';
import type { OvertimeRecordType, OvertimeReportResult, OvertimeRecord } from '@/api/domains/overtime/overtimeReport.types';
import type { TableColumn } from '@/components/ui/tables/Table';

/**
 * Filters for overtime report
 */
export interface OvertimeReportFilters {
  year: number;
  month: number;
  monthlyCutId: number;
  monthlyCutCode: string;
  type: OvertimeRecordType;
}

/**
 * Expanded field for table row details
 */
export interface ExpandedField {
  label: string;
  value: string;
}


export const useOvertimeReport = () => {
  const { t: translate } = useTranslation('attendance');
  const { showApiError } = useUI();

  // Initialize filters with current date defaults
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [filters, setFilters] = useState<OvertimeReportFilters>({
    year: currentYear,
    month: currentMonth,
    monthlyCutId: 0,
    monthlyCutCode: `${currentYear}${currentMonth.toString().padStart(2, '0')}`,
    type: 'Todos',
  });

  // Use composed hooks for different concerns
  const {
    loadingMonthlyCuts,
    monthlyCutsWithinRange,
    yearOptions,
    monthOptions,
  } = useMonthlyCutsLoader();

  // Table columns configuration
  const columns: TableColumn<OvertimeRecord>[] = useMemo(
    () => [
      {
        key: 'type',
        header: translate('overtimeReport.table.type'),
        sortable: true,
      },
      {
        key: 'date',
        header: translate('overtimeReport.table.date'),
        sortable: true,
      },
      {
        key: 'schedule',
        header: translate('overtimeReport.table.schedule'),
        sortable: true,
      },
      {
        key: 'hoursFormatted',
        header: translate('overtimeReport.table.hoursFormatted'),
        sortable: true,
      },
      {
        key: 'hoursDecimal',
        header: translate('overtimeReport.table.hoursDecimal'),
        sortable: true,
        render: (record: OvertimeRecord) => record.hoursDecimal.toFixed(4),
      },
    ],
    [translate],
  );

  // Expandable row configuration
  const getExpandedFields = useCallback((record: OvertimeRecord): ExpandedField[] => {
    const fields: ExpandedField[] = [];
    const isHHEE = record.type === 'HHEE';
    const isAtraso = record.type === 'Atraso' || record.type === 'Atrasos';

    if (isHHEE) {
      // Si es HHEE: mostrar solo HHEE T1, T2, T3 (y solo si > 0:00:00)
      if (record.concepts.overtimeT1.timeFormatted !== '0:00:00' && record.concepts.overtimeT1.decimalValue > 0) {
        fields.push({
          label: translate('overtimeReport.table.concepts.overtimeT1'),
          value: `${record.concepts.overtimeT1.timeFormatted} / ${record.concepts.overtimeT1.decimalValue.toFixed(4)}`,
        });
      }

      if (record.concepts.overtimeT2.timeFormatted !== '0:00:00' && record.concepts.overtimeT2.decimalValue > 0) {
        fields.push({
          label: translate('overtimeReport.table.concepts.overtimeT2'),
          value: `${record.concepts.overtimeT2.timeFormatted} / ${record.concepts.overtimeT2.decimalValue.toFixed(4)}`,
        });
      }

      if (record.concepts.overtimeT3.timeFormatted !== '0:00:00' && record.concepts.overtimeT3.decimalValue > 0) {
        fields.push({
          label: translate('overtimeReport.table.concepts.overtimeT3'),
          value: `${record.concepts.overtimeT3.timeFormatted} / ${record.concepts.overtimeT3.decimalValue.toFixed(4)}`,
        });
      }
    } else if (isAtraso) {
      // Si es Atraso: mostrar Atraso, Ausencia entre jornada, Salida anticipada (solo si > 0:00:00)
      if (record.concepts.delay.timeFormatted !== '0:00:00' && record.concepts.delay.decimalValue > 0) {
        fields.push({
          label: translate('overtimeReport.table.concepts.delay'),
          value: `${record.concepts.delay.timeFormatted} / ${record.concepts.delay.decimalValue.toFixed(4)}`,
        });
      }

      if (record.concepts.absenceBetween.timeFormatted !== '0:00:00' && record.concepts.absenceBetween.decimalValue > 0) {
        fields.push({
          label: translate('overtimeReport.table.concepts.absenceBetween'),
          value: `${record.concepts.absenceBetween.timeFormatted} / ${record.concepts.absenceBetween.decimalValue.toFixed(4)}`,
        });
      }

      if (record.concepts.earlyDeparture.timeFormatted !== '0:00:00' && record.concepts.earlyDeparture.decimalValue > 0) {
        fields.push({
          label: translate('overtimeReport.table.concepts.earlyDeparture'),
          value: `${record.concepts.earlyDeparture.timeFormatted} / ${record.concepts.earlyDeparture.decimalValue.toFixed(4)}`,
        });
      }
    }

    return fields;
  }, [translate]);

  const rowExpandable = useCallback((record: OvertimeRecord) => {
    const isHHEE = record.type === 'HHEE';
    const isAtraso = record.type === 'Atraso' || record.type === 'Atrasos';

    if (isHHEE) {
      // Expandible si tiene algún valor HHEE > 0
      return (
        record.concepts.overtimeT1.decimalValue > 0 ||
        record.concepts.overtimeT2.decimalValue > 0 ||
        record.concepts.overtimeT3.decimalValue > 0
      );
    }

    if (isAtraso) {
      // Expandible si tiene algún concepto de atraso > 0
      return (
        record.concepts.delay.decimalValue > 0 ||
        record.concepts.absenceBetween.decimalValue > 0 ||
        record.concepts.earlyDeparture.decimalValue > 0
      );
    }

    // SinRegistro y otros tipos: chevron visible pero disabled
    return false;
  }, []);

  // Report data state
  const [reportData, setReportData] = useState<OvertimeReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('');
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [shouldFetch, setShouldFetch] = useState(false);

  // Trigger search with current filters
  const handleSearch = useCallback(() => {
    // Reset pagination and sort when starting a new search
    setPage(1);
    setOrderBy('');
    setOrderDirection('ASC');
    setShouldFetch(true);
  }, []);

  const fetchReport = useCallback(async () => {
    // Validate required filters before fetching
    if (!shouldFetch || !filters.monthlyCutId || filters.monthlyCutId <= 0) {
      setShouldFetch(false);
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await overtimeReportService.getReport({
        year: filters.year,
        month: filters.month,
        monthlyCutId: filters.monthlyCutId,
        type: filters.type,
        page,
        pageSize,
        orderBy,
        orderDirection,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('overtimeReport.errors.loadFailed'));
        setReportData(null);
        return;
      }

      setReportData(response.data);
    } finally {
      setIsLoading(false);
      setShouldFetch(false);
    }
  }, [
    shouldFetch,
    filters.year,
    filters.month,
    filters.monthlyCutId,
    filters.type,
    page,
    pageSize,
    orderBy,
    orderDirection,
    showApiError,
    translate,
  ]);

  useEffect(() => {
    if (shouldFetch) {
      void fetchReport();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  const onSortChange = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      setOrderBy(columnKey);
      setOrderDirection(direction.toUpperCase() as 'ASC' | 'DESC');
      setPage(1);
      setShouldFetch(true);
    },
    [],
  );

  // Filter monthly cuts by selected year and month for dropdown
  const monthlyCutOptions = useMemo(() => {
    const options = monthlyCutsWithinRange
      .filter(cut => {
        // Use the month and year from backend
        if (filters.year && cut.year !== filters.year) return false;
        if (filters.month && cut.month !== filters.month) return false;
        return true;
      })
      .sort((a, b) => {
        // Sort by year and month (most recent first)
        if (a.year !== b.year) {
          return (b.year ?? 0) - (a.year ?? 0);
        }
        return (b.month ?? 0) - (a.month ?? 0);
      })
      .map(cut => ({
        value: cut.monthlyCutId.toString(),
        label: cut.label,
        supportingText: `${cut.startDate} - ${cut.endDate}`,
      }));

    return options;
  }, [monthlyCutsWithinRange, filters.year, filters.month]);

  // Auto-select first monthly cut when year/month changes
  useEffect(() => {
    if (filters.year && filters.month && monthlyCutOptions.length > 0 && !filters.monthlyCutId) {
      const firstCutIdStr = monthlyCutOptions[0]?.value;
      const firstCutId = firstCutIdStr ? parseInt(firstCutIdStr, 10) : 0;
      if (firstCutId) {
        setFilters(previous => ({
          ...previous,
          monthlyCutId: firstCutId,
        }));
      }
    }
  }, [filters.year, filters.month, filters.monthlyCutId, monthlyCutOptions, setFilters]);

  // Get month options for selected year
  const currentMonthOptions = useMemo(
    () => monthOptions(filters.year),
    [monthOptions, filters.year],
  );

  return {
    // Filters
    filters,
    setFilters,
    handleSearch,
    // Data
    reportData,
    records: reportData?.records ?? [],
    indicators: reportData?.indicators ?? null,
    totalRecords: reportData?.totalRecords ?? 0,
    isLoading,
    // Monthly cuts
    loadingMonthlyCuts,
    monthlyCutOptions,
    yearOptions,
    monthOptions: currentMonthOptions,
    // Pagination
    page,
    pageSize,
    totalPages: reportData?.totalPages ?? 1,
    setPage: (newPage: number) => {
      setPage(newPage);
      setShouldFetch(true);
    },
    setPageSize: (newSize: number) => {
      setPageSize(newSize);
      setPage(1);
      setShouldFetch(true);
    },
    // Sort
    onSortChange,
    orderDirection,
    // Table config
    columns,
    // Expandable rows
    getExpandedFields,
    rowExpandable,
    // i18n
    translate,
  };
};
